import { IRedditMentionRepository } from '../../domain/interfaces/IRedditMentionRepository';
import {
  RedditMention,
  RedditMentionDto,
  GetAllMentionsQuery,
  ConfirmMatchCommand,
  RejectMatchCommand,
  UpdateMentionCommand,
  BusinessMentionsResponse,
  BackendRedditMentionSummary,
  MappedBusinessMentionsResponse,
  BackendMentionListItem,
  ConfirmMentionRequest,
  ConfirmMentionResponse,
  DismissMentionResponse
} from '../../domain/entities/RedditMention';
import { PaginatedResult } from '../../domain/common/PaginatedResult';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';
import { EmotionalTone } from '../../domain/value-objects/EmotionalTone';
import { MatchStatus } from '../../domain/value-objects/MatchStatus';

/**
 * RedditMentionRepository - Implementation of Reddit mention repository
 *
 * Handles all API calls related to Reddit mentions
 */
export class RedditMentionRepository implements IRedditMentionRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Maps backend RedditMentionSummary to frontend RedditMentionDto
   * Transforms field names from backend C# conventions to frontend expectations
   */
  private mapBackendToDto(backend: BackendRedditMentionSummary): RedditMentionDto {
    return {
      id: backend.id,
      redditPostId: backend.postId,
      subreddit: backend.sourceSubreddit,
      postTitle: backend.postTitle,
      content: backend.extractedContextPreview,
      postPermalink: backend.postUrl,
      authorUsername: backend.postAuthor,
      postCreatedAt: backend.postCreatedAt,
      businessId: null,
      businessName: backend.extractedBusinessName,
      upvoteCount: backend.score ?? 0,
      commentCount: backend.commentCount ?? 0,
      isComment: false,
      matchConfidenceScore: backend.matchConfidence,
      matchStatus: backend.isVerified ? ('Verified' as MatchStatus) : ('Matched' as MatchStatus),
      reviewStatus: backend.isVerified ? 'Approved' : 'Pending',
      emotionalToneScore: backend.emotionalToneScore,
      emotionalTone: backend.emotionalTone as EmotionalTone | null,
      processingStatus: 'Processed',
      createdAt: backend.postCreatedAt
    };
  }

  /**
   * Maps backend MentionListItem to frontend RedditMentionDto
   * Handles field name differences between backend C# and frontend TypeScript
   * Used by getAll() method which receives MentionListItem from GetAllMentionsQuery
   */
  private mapMentionListItemToDto(item: BackendMentionListItem): RedditMentionDto {
    return {
      id: item.id,
      redditPostId: item.redditPostId,
      subreddit: item.subreddit,
      postTitle: item.postTitle,
      content: item.content, // Use actual content where mention was found (Session 008 fix)
      postPermalink: item.postUrl,
      authorUsername: item.postAuthor,
      postCreatedAt: item.postCreatedAt,

      // Business relationship - MAP FIELD NAMES
      businessId: item.matchedBusinessId,
      businessName: item.matchedBusinessName,

      // Scores
      upvoteCount: item.score ?? 0,
      commentCount: item.commentCount ?? 0,
      isComment: false,

      // Match confidence - CONVERT TO PERCENTAGE (0.0-1.0 → 0-100)
      matchConfidenceScore: item.matchConfidence !== null && item.matchConfidence !== undefined
        ? item.matchConfidence * 100
        : 0,

      // Map backend status (Pending/Confirmed/Dismissed) to MatchStatus (Unmatched/Matched/Verified/Rejected)
      // Backend Session 003: status field with new values
      matchStatus: item.status === 'Confirmed'
        ? ('Verified' as MatchStatus)
        : item.status === 'Dismissed'
          ? ('Rejected' as MatchStatus)
          : item.matchedBusinessId
            ? ('Matched' as MatchStatus)
            : ('Unmatched' as MatchStatus),

      // Map backend status to reviewStatus (SESSION_045: Added ReviewStatus for manual admin review workflow)
      // Backend: 'Pending' | 'Confirmed' | 'Dismissed' → Frontend: 'Pending' | 'Approved' | 'Rejected'
      reviewStatus: item.status === 'Confirmed'
        ? 'Approved'
        : item.status === 'Dismissed'
          ? 'Rejected'
          : 'Pending',

      // Emotional tone - MAP FIELD NAMES
      emotionalToneScore: item.sentimentScore,
      emotionalTone: item.sentimentLabel as EmotionalTone | null,

      processingStatus: item.isProcessed ? 'Processed' : 'Pending',
      createdAt: item.createdAt
    };
  }

  /**
   * Get all mentions with pagination and filtering
   * Maps backend MentionListItem response to RedditMentionDto
   */
  async getAll(query?: GetAllMentionsQuery): Promise<PaginatedResult<RedditMentionDto>> {
    const response = await this.apiClient.get<PaginatedResult<BackendMentionListItem>>(
      API_ENDPOINTS.REDDIT_MENTIONS.BASE,
      { params: query }
    );

    // Transform backend MentionListItem[] to RedditMentionDto[]
    return {
      ...response,
      items: response.items.map(item => this.mapMentionListItemToDto(item))
    };
  }

  /**
   * Get a single mention by ID
   */
  async getById(id: string): Promise<RedditMention> {
    return this.apiClient.get<RedditMention>(API_ENDPOINTS.REDDIT_MENTIONS.BY_ID(id));
  }

  /**
   * Get mentions for a specific business
   * Backend uses dedicated endpoint: GET /api/reddit-mentions/business/{businessId}
   * Returns MappedBusinessMentionsResponse with mentions mapped from backend format
   */
  async getByBusiness(
    businessId: string,
    query?: GetAllMentionsQuery
  ): Promise<MappedBusinessMentionsResponse> {
    const response = await this.apiClient.get<BusinessMentionsResponse>(
      API_ENDPOINTS.REDDIT_MENTIONS.BY_BUSINESS(businessId),
      { params: query }
    );

    // Map backend RedditMentionSummary[] to frontend RedditMentionDto[]
    return {
      ...response,
      mentions: response.mentions.map(m => this.mapBackendToDto(m))
    };
  }

  /**
   * Create a new mention (admin only)
   */
  async create(mention: Partial<RedditMention>): Promise<RedditMention> {
    return this.apiClient.post<RedditMention>(API_ENDPOINTS.REDDIT_MENTIONS.BASE, mention);
  }

  /**
   * Update an existing mention
   */
  async update(id: string, command: UpdateMentionCommand): Promise<RedditMention> {
    return this.apiClient.put<RedditMention>(API_ENDPOINTS.REDDIT_MENTIONS.BY_ID(id), command);
  }

  /**
   * Delete a mention (admin only)
   */
  async delete(id: string): Promise<void> {
    await this.apiClient.delete<void>(API_ENDPOINTS.REDDIT_MENTIONS.BY_ID(id));
  }

  /**
   * Confirm a business match
   * Backend uses PUT /api/reddit-mentions/{id}/verify with isVerified: true
   */
  async confirmMatch(command: ConfirmMatchCommand): Promise<void> {
    await this.apiClient.put<void>(
      API_ENDPOINTS.REDDIT_MENTIONS.VERIFY(command.mentionId),
      {
        id: command.mentionId,
        isVerified: true,
        businessId: command.businessId
      }
    );
  }

  /**
   * Reject a business match
   * Backend uses PUT /api/reddit-mentions/{id}/verify with isVerified: false
   */
  async rejectMatch(command: RejectMatchCommand): Promise<void> {
    await this.apiClient.put<void>(
      API_ENDPOINTS.REDDIT_MENTIONS.VERIFY(command.mentionId),
      {
        id: command.mentionId,
        isVerified: false,
        rejectionReason: command.reason
      }
    );
  }

  /**
   * Process unmatched mentions (admin only)
   */
  async processUnmatched(): Promise<void> {
    await this.apiClient.post<void>(`${API_ENDPOINTS.REDDIT_MENTIONS.BASE}/process-unmatched`);
  }

  /**
   * Recalculate match confidence for a mention
   */
  async recalculateMatchConfidence(id: string): Promise<RedditMention> {
    return this.apiClient.post<RedditMention>(
      `${API_ENDPOINTS.REDDIT_MENTIONS.BY_ID(id)}/recalculate-confidence`
    );
  }

  /**
   * Confirm a mention and link it to a business (Session 003)
   * POST /api/reddit-mentions/{id}/confirm
   * Requires Admin role
   */
  async confirm(mentionId: string, businessId: string): Promise<ConfirmMentionResponse> {
    const request: ConfirmMentionRequest = {
      businessId
    };

    return this.apiClient.post<ConfirmMentionResponse>(
      API_ENDPOINTS.REDDIT_MENTIONS.CONFIRM(mentionId),
      request
    );
  }

  /**
   * Dismiss a mention as a false positive (Session 003)
   * POST /api/reddit-mentions/{id}/dismiss
   * Requires Admin role
   */
  async dismiss(mentionId: string): Promise<DismissMentionResponse> {
    return this.apiClient.post<DismissMentionResponse>(
      API_ENDPOINTS.REDDIT_MENTIONS.DISMISS(mentionId)
    );
  }
}
